import React from "react";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

/**
 * Shared clue strip for play, recording, and screenshot scenes.
 * `emphasized` is the App Store /02 treatment (larger type, gold glow).
 */
export default function ClueBanner({
    clueText,
    hasClue = false,
    emphasized = false,
    compact = false,
    flush = false,
    screenshot = false,
    overlay = null,
    textColor = "#1c1917",
    placeholderColor = "rgba(15, 61, 54, 0.55)",
}) {
    const iconPx = screenshot || emphasized
        ? "clamp(30px, 9cqmin, 42px)"
        : compact
            ? "clamp(22px, 10cqmin, 32px)"
            : 32;
    const iconFont = screenshot || emphasized
        ? "clamp(18px, 6.5cqmin, 26px)"
        : compact
            ? "clamp(14px, 7cqmin, 20px)"
            : 20;
    const labelFont = screenshot || emphasized
        ? "clamp(10px, 3.2cqmin, 13px)"
        : compact
            ? "clamp(7px, 3cqmin, 10px)"
            : "0.65rem";
    const bodyFont = screenshot || emphasized
        ? "clamp(15px, 4.6cqmin, 20px)"
        : compact
            ? "clamp(10px, 5cqmin, 14px)"
            : "1rem";

    const blockCopy = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    return (
        <Box
            draggable={false}
            onCopy={blockCopy}
            onCut={blockCopy}
            onDragStart={blockCopy}
            onContextMenu={blockCopy}
            sx={{
                flexShrink: 0,
                position: "relative",
                zIndex: emphasized || screenshot ? 3 : 1,
                mt: flush || compact || emphasized || screenshot ? 0 : 1.5,
                px: screenshot || emphasized ? 1.35 : compact ? 1 : 1.5,
                py: screenshot || emphasized ? 1.6 : compact ? 0.55 : 1.35,
                mx: emphasized || compact || flush || screenshot ? 0 : 0.5,
                borderRadius: emphasized || screenshot ? 2.5 : 2,
                bgcolor: "rgba(255, 251, 235, 0.98)",
                border: emphasized || screenshot
                    ? "2.5px solid #f59e0b"
                    : "1.5px solid rgba(245, 158, 11, 0.85)",
                boxShadow: emphasized || screenshot
                    ? [
                          "0 0 0 3px rgba(251, 191, 36, 0.45)",
                          "0 10px 28px rgba(180, 83, 9, 0.42)",
                          "0 0 36px rgba(251, 191, 36, 0.5)",
                      ].join(", ")
                    : "0 4px 14px rgba(180, 83, 9, 0.18)",
                minHeight: screenshot || emphasized ? 84 : compact ? 40 : 60,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: screenshot || emphasized ? 1.15 : 0.85,
                maxWidth: "100%",
                width: "100%",
                alignSelf: "center",
                boxSizing: "border-box",
                overflow: "hidden",
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                WebkitTouchCallout: "none",
                WebkitUserDrag: "none",
                cursor: "default",
                "& *": {
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    WebkitTouchCallout: "none",
                    WebkitUserDrag: "none",
                },
                "&::selection, & *::selection": {
                    background: "transparent",
                },
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    width: iconPx,
                    height: iconPx,
                    borderRadius: "50%",
                    bgcolor: "#f59e0b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 2px 8px rgba(180, 83, 9, 0.45)",
                }}
            >
                <SearchIcon sx={{ color: "#fffbeb", fontSize: iconFont }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    component="span"
                    sx={{
                        display: "block",
                        fontSize: labelFont,
                        fontWeight: 800,
                        letterSpacing: "0.16em",
                        color: "#b45309",
                        textTransform: "uppercase",
                        lineHeight: 1,
                        mb: 0.35,
                    }}
                >
                    Clue
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: hasClue ? textColor : placeholderColor,
                        lineHeight: 1.28,
                        fontWeight: hasClue ? 800 : 500,
                        fontStyle: hasClue ? "normal" : "italic",
                        textAlign: "left",
                        fontSize: bodyFont,
                        display: "-webkit-box",
                        WebkitLineClamp: emphasized ? 3 : 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                    }}
                >
                    {clueText}
                </Typography>
            </Box>
            {overlay}
        </Box>
    );
}
